/*
 * Offer test: the product page receipt.
 *
 * Shows, per painting, what adding the painting on this page does to the
 * price. The prices come from offer-test-pricing.js, the same module the
 * side cart uses, on top of the stamping engine's rule. Nothing here knows
 * the offers' percentages.
 *
 * Liquid renders the receipt (snippets/offer-test-receipt.liquid): every
 * header state, the ghost rows, and <template>s for the rows. This script
 * never builds markup and never draws the arm. It reads <html data-offer-arm>,
 * clones the row templates, fills their text, and sets data-m (paintings
 * after this one: 1, 2, 3, or 4 for 4 or more). CSS shows the header for
 * the state.
 *
 * Load it without defer, before the receipt markup: the receipt draws itself
 * from an inline call at the end of its markup, so its first paint is
 * already right. A DOM-ready pass catches a receipt the inline call missed.
 *
 * Draws again on a variant change and on every `side-cart:updated`.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./offer-test-pricing.js'));
  else root.OmdOfferReceipt = factory(null, root);
})(typeof self !== 'undefined' ? self : this, function (pricingModule, root) {
  var THIS_KEY = '__offer_receipt_this__';
  var MAX_ROWS = 3;
  var SIZE = /\d\s*[x×]\s*\d/i;

  function pricing() {
    return pricingModule || root.OmdOfferPricing;
  }

  function toInput(line) {
    return { key: line.key, quantity: line.quantity, price: line.price, stamped: !!line.stamped };
  }

  function kindOf(percent) {
    return percent > 0 ? 'off' : 'full';
  }

  // A cart line is its variant plus its exact properties: Shopify joins two
  // lines whose properties match, and keeps two that differ. A blank value
  // counts. Shopify keeps `_offer: ""`, so a line with a blank stamp never
  // joins a line without one (measured 4 Oct).
  function groupOf(variantId, properties) {
    var props = properties || {};
    var kept = Object.keys(props).filter(function (name) {
      return props[name] !== null && props[name] !== undefined;
    }).sort().map(function (name) { return [name, String(props[name])]; });
    return String(variantId) + '|' + JSON.stringify(kept);
  }

  // The cart line a part of `line` ends up on once the stamping engine has
  // written the stamps. A stamped part carries the stamp. An unstamped part
  // keeps the line's properties as they are, unless the engine writes that
  // line (it splits it, or clears its stamp): then its stamp is blank.
  // Null for a line without its variant and properties.
  function lineAfter(line, value, split) {
    if (line.variant_id === undefined || line.variant_id === null || !line.properties) return null;
    var props = {};
    Object.keys(line.properties).forEach(function (name) { props[name] = line.properties[name]; });
    if (value) props._offer = value;
    else if (split || line.stamped) props._offer = '';
    return groupOf(line.variant_id, props);
  }

  // What the receipt shows once the painting on this page is added.
  //   cart: the painting lines in the cart now, in cart order:
  //         { key, quantity, price (unit, cents, before discount), stamped,
  //           variant_id and properties (optional, to join rows the way the
  //           cart will join its lines) }
  //   item: the painting on this page, one unit: { price }
  // Other fields on a line or the item ride along untouched as `source`.
  function plan(arm, cart, item) {
    var lines = (cart || []).filter(function (line) { return line.quantity > 0; });
    var byKey = {};
    var n = 0;
    lines.forEach(function (line) {
      byKey[line.key] = line;
      n += line.quantity;
    });

    // The store puts a newly added line first in the cart, and the rule
    // breaks price ties by cart order. Price the set the way it will be.
    var added = { key: THIS_KEY, quantity: 1, price: item.price, stamped: false };
    var after = pricing().price(arm, [added].concat(lines.map(toInput)));
    var before = pricing().price(arm, lines.map(toInput));
    var was = {};
    before.lines.forEach(function (line) { was[line.key] = line.final; });

    var add = null;
    var rows = [];
    after.lines.forEach(function (line) {
      var isThis = line.key === THIS_KEY;
      // How much this painting changes the line's price (negative when it
      // goes up): what decides which
      // rows show when there are too many.
      var change = isThis ? 0 : (was[line.key] || 0) - line.final;
      var split = line.parts.length > 1;
      line.parts.forEach(function (part) {
        var row = {
          source: isThis ? item : byKey[line.key],
          quantity: part.quantity,
          original: part.original,
          final: part.final,
          percent: part.percent,
          kind: kindOf(part.percent),
          change: change,
          line: isThis ? null : lineAfter(byKey[line.key], part.value, split)
        };
        if (isThis) add = row;
        else rows.push(row);
      });
    });
    if (n === 0) add.kind = 'first';

    // One row per cart line, as the cart will have them once the stamps
    // land. Two lines of one painting that end up with the same properties
    // become one line (Shopify joins them), so they show as one row. Lines
    // that keep different properties stay two rows, even at the same price.
    var grouped = {};
    rows = rows.filter(function (row) {
      var id = row.line;
      if (!id) return true;
      var same = grouped[id];
      if (!same) {
        grouped[id] = row;
        return true;
      }
      same.quantity += row.quantity;
      same.original += row.original;
      same.final += row.final;
      same.change += row.change;
      return false;
    });

    // At most MAX_ROWS cart rows: the ones this painting changes most (up or
    // down), then the most discounted, shown in cart order. The rest fold
    // into one "+ k more paintings" row.
    var shown = rows;
    var more = 0;
    if (rows.length > MAX_ROWS) {
      var keep = rows
        .map(function (row, index) { return { row: row, index: index, off: row.original - row.final }; })
        .sort(function (a, b) { return Math.abs(b.row.change) - Math.abs(a.row.change) || b.off - a.off || a.index - b.index; })
        .slice(0, MAX_ROWS)
        .map(function (entry) { return entry.index; });
      shown = rows.filter(function (row, index) { return keep.indexOf(index) !== -1; });
      rows.forEach(function (row, index) { if (keep.indexOf(index) === -1) more += row.quantity; });
    }

    return {
      n: n,
      m: n + 1,
      rows: shown,
      more: more,
      add: add,
      original: after.original,
      final: after.final
    };
  }

  // Same rules as snippets/side-cart-short-text.liquid, so a painting reads
  // the same here as in the side cart.
  function shorten(text, suffix, markers) {
    var short = String(text || '').trim();
    var end = String(suffix || '').trim().toLowerCase();
    if (end && short.length > end.length && short.slice(-end.length).toLowerCase() === end) {
      var head = short.slice(0, short.length - end.length).trim();
      if (head) short = head;
    }
    String(markers || '').split(',').forEach(function (raw) {
      var marker = raw.trim();
      if (!marker || short.indexOf(marker) === -1) return;
      var cut = short.split(marker)[0].trim();
      if (cut) short = cut;
    });
    return short;
  }

  // "40x50 · Los canvas": the size option without "cm", then the first other
  // option. Option names are translated, so the size is found by its value.
  function describe(options, markers) {
    var size = '';
    var rest = [];
    (options || []).forEach(function (value) {
      var text = shorten(value, '', markers);
      if (!text) return;
      if (!size && SIZE.test(text)) size = text.replace(/\s*cm\b/i, '').trim();
      else rest.push(text);
    });
    return { size: size, finish: rest[0] || '' };
  }

  // Money in the cart's currency and format: Liquid prints 1111.11 with the
  // `money` filter, and the same separators and symbol are used here.
  function moneyFormat(sample) {
    var text = String(sample || '').replace(/\s+/g, ' ').trim();
    var full = /^(.*?)1([^0-9]?)111([^0-9])11(.*)$/.exec(text);
    var whole = full ? null : /^(.*?)1([^0-9]?)111(.*)$/.exec(text);
    function group(value, separator) {
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    }
    return function (cents) {
      cents = Math.round(Number(cents) || 0);
      if (full) {
        var units = Math.floor(cents / 100);
        var rest = String(cents % 100);
        if (rest.length < 2) rest = '0' + rest;
        return full[1] + group(units, full[2]) + full[3] + rest + full[4];
      }
      if (whole) return whole[1] + group(Math.round(cents / 100), whole[2]) + whole[3];
      return (cents / 100).toFixed(2);
    };
  }

  // The shopper's own photo, from the line the photo builder added. It shows in the
  // receipt on the new custom photo page only; every other page keeps the product image.
  // Liquid's `item.properties | json` gives [name, value] pairs; `/cart.js` gives an object.
  var PHOTO = '_dot_painting_cropped_url';
  function photoOf(line) {
    var props = line && line.properties;
    var url = '';
    if (Array.isArray(props)) {
      props.forEach(function (pair) { if (Array.isArray(pair) && pair[0] === PHOTO) url = pair[1]; });
    } else if (props) {
      url = props[PHOTO];
    }
    return typeof url === 'string' && /^(https?:)?\/\//.test(url) ? url : '';
  }

  if (!root || !root.document) {
    return { plan: plan, groupOf: groupOf, shorten: shorten, describe: describe, moneyFormat: moneyFormat, photoOf: photoOf, THIS_KEY: THIS_KEY, MAX_ROWS: MAX_ROWS };
  }

  // ---------- Browser ----------

  var document = root.document;
  var html = document.documentElement;
  var receipts = [];
  var bound = false;
  // The side cart writes the painting line keys into its markup on every
  // render, from the same cart it sends with `side-cart:updated`.
  var COUNT_SOURCE = 'side-cart-drawer [data-offer-test-count]';

  function arm() {
    return html.getAttribute('data-offer-arm') || '';
  }

  function sizedImage(url) {
    if (!url) return '';
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=68';
  }

  function onNewPage() {
    return html.getAttribute('data-pdp-page') === 'new';
  }

  // A painting line from `/cart.js`, in the shape the receipt reads.
  function fromCart(item) {
    return {
      key: item.key,
      variant_id: item.variant_id,
      quantity: item.quantity,
      price: item.original_price,
      stamped: !!(item.properties && item.properties._offer !== undefined && item.properties._offer !== null && String(item.properties._offer) !== ''),
      properties: item.properties || {},
      title: item.product_title,
      options: (item.options_with_values || []).map(function (option) { return option.value; }),
      image: sizedImage(item.image || (item.featured_image && item.featured_image.url))
    };
  }

  function Receipt(el) {
    var data = JSON.parse(el.querySelector('[data-offer-receipt-data]').textContent);
    var money = el.querySelector('template[data-offer-receipt-money]');
    // Translations sit in attributes: the browser decodes them once, so a
    // translated apostrophe or ampersand shows as itself.
    var text = el.querySelector('template[data-offer-receipt-text]');
    this.el = el;
    this.data = data;
    this.text = {};
    ['off', 'full', 'first', 'from', 'starts', 'more-one', 'more-other', 'add'].forEach(function (name) {
      this.text[name] = text.getAttribute('data-' + name) || '';
    }, this);
    this.money = moneyFormat(money ? money.content.textContent : '');
    // One thumbnail URL per variant. `/cart.js` gives the same image on
    // another host, and a changed URL would redraw an unchanged receipt.
    this.images = {};
    data.product.variants.forEach(function (variant) { this.images[variant.id] = variant.image || data.product.image; }, this);
    data.cart.forEach(function (line) { if (line.image) this.images[line.variant_id] = line.image; }, this);
    this.cart = data.cart;
    this.variants = {};
    data.product.variants.forEach(function (variant) { this.variants[variant.id] = variant; }, this);
    this.variant = this.variants[data.variant] || data.product.variants[0];
    this.scope = el.closest('.product-detail') || document;
    this.rowTemplate = el.querySelector('template[data-offer-receipt-row]');
    this.moreTemplate = el.querySelector('template[data-offer-receipt-more]');
    this.cartList = el.querySelector('[data-offer-receipt-cart]');
    this.addList = el.querySelector('[data-offer-receipt-add]');
    this.was = el.querySelector('[data-offer-receipt-was]');
    this.before = el.querySelector('[data-offer-receipt-before]');
    this.after = el.querySelector('[data-offer-receipt-after]');
    this.sumFrom = el.querySelector('[data-offer-receipt-from]');
    this.drawn = '';
  }

  Receipt.prototype.item = function () {
    var variant = this.variant;
    return {
      price: variant.price,
      title: this.data.product.title,
      options: variant.options,
      image: this.images[variant.id]
    };
  };

  Receipt.prototype.setVariant = function (id) {
    var variant = this.variants[id];
    // A pinned receipt (custom photo page) always prices its own variant.
    if (this.data.pinned || !variant || variant === this.variant) return;
    this.variant = variant;
    this.draw();
  };

  Receipt.prototype.setCart = function (lines) {
    this.cart = lines.map(function (line) {
      if (this.images[line.variant_id]) line.image = this.images[line.variant_id];
      else if (line.image) this.images[line.variant_id] = line.image;
      return line;
    }, this);
    this.draw();
  };

  // The word before the price in the translated "from {{ price }}" string
  // ("vanaf", "from"). Every locale puts the price last. The price itself keeps
  // its own elements, so the struck and discounted prices still show.
  Receipt.prototype.fromText = function () {
    return this.text.from.replace('[price]', '').trim();
  };

  Receipt.prototype.label = function (row, n, isAdd) {
    var text = this.text;
    var source = row.source;
    var detail = describe(source.options, this.data.markers);
    var line = detail.size && detail.finish ? detail.size + ' · ' + detail.finish : detail.size || detail.finish;
    if (n === 0 && row.kind === 'first') line = text.starts.replace('[size]', detail.size).replace(/^\s*·\s*/, '');
    var tag = '';
    if (row.kind === 'off') tag = text.off.replace('[percent]', row.percent);
    else if (row.kind === 'first') tag = text.first;
    else tag = text.full;
    var photo = onNewPage() ? photoOf(source) : '';
    return {
      kind: row.kind,
      name: shorten(source.title, this.data.suffix, ''),
      quantity: row.quantity > 1 ? '×' + row.quantity : '',
      detail: line,
      // The short name the compact box shows: the size, or "Add this one" for this painting.
      size: isAdd ? text.add : detail.size,
      tag: tag,
      was: this.money(row.original),
      now: this.money(row.final),
      image: (photo && sizedImage(photo)) || source.image || '',
      photo: !!photo,
      from: isAdd && this.data.from ? this.fromText() : ''
    };
  };

  Receipt.prototype.fill = function (list, rows, place, more) {
    var nodes = rows.map(function (row) {
      var node = this.rowTemplate.content.firstElementChild.cloneNode(true);
      node.setAttribute('data-place', place);
      node.setAttribute('data-kind', row.kind);
      node.querySelector('[data-name]').textContent = row.name;
      node.querySelector('[data-qty]').textContent = row.quantity;
      node.querySelector('[data-detail]').textContent = row.detail;
      node.querySelector('[data-size]').textContent = row.size;
      node.querySelector('[data-tag]').textContent = row.tag;
      var from = node.querySelector('[data-from]');
      from.textContent = row.from;
      from.hidden = !row.from;
      node.querySelector('[data-was]').textContent = row.was;
      node.querySelector('[data-now]').textContent = row.now;
      var image = node.querySelector('[data-image]');
      if (row.image) image.setAttribute('src', row.image);
      else image.parentNode.removeChild(image);
      // The shopper's own photo is kept out of session replay and heatmaps, like every
      // other surface that shows it (see snippets/posthog-init.liquid).
      if (row.image && row.photo) image.classList.add('ph-no-capture');
      return node;
    }, this);
    if (more) {
      var extra = this.moreTemplate.content.firstElementChild.cloneNode(true);
      extra.textContent = more === 1 ? this.text['more-one'] : this.text['more-other'].replace('[count]', more);
      nodes.push(extra);
    }
    while (list.firstChild) list.removeChild(list.firstChild);
    nodes.forEach(function (node) { list.appendChild(node); });
  };

  Receipt.prototype.draw = function () {
    var current = arm();
    if (!current) return false;
    var result = plan(current, this.cart, this.item());
    var view = {
      m: Math.min(result.m, 4),
      rows: result.rows.map(function (row) { return this.label(row, result.n); }, this),
      more: result.more,
      add: this.label(result.add, result.n, true),
      before: result.final < result.original ? this.money(result.original) : '',
      after: this.money(result.final),
      from: this.data.from ? this.fromText() : ''
    };
    // One cart change sends `side-cart:updated` several times. Only a real
    // change redraws, so the live region is not read out again for nothing.
    var signature = JSON.stringify(view);
    if (signature === this.drawn) return true;
    this.drawn = signature;

    this.el.setAttribute('data-m', String(view.m));
    this.fill(this.cartList, view.rows, 'cart', view.more);
    this.fill(this.addList, [view.add], 'add', 0);
    // The set price before is struck only when this painting lowers it.
    this.was.hidden = !view.before;
    this.before.textContent = view.before;
    this.after.textContent = view.after;
    this.sumFrom.textContent = view.from;
    this.sumFrom.hidden = !view.from;
    return true;
  };

  function readPaintingKeys() {
    var node = document.querySelector(COUNT_SOURCE);
    if (!node) return null;
    try {
      var data = JSON.parse(node.textContent);
      return Array.isArray(data.keys) ? data.keys : null;
    } catch (error) {
      return null;
    }
  }

  function bind() {
    if (bound) return;
    bound = true;

    root.addEventListener('side-cart:updated', function (event) {
      var cart = event.detail && event.detail.cart;
      var keys = readPaintingKeys();
      if (!cart || !cart.items || !keys) return;
      var lines = cart.items
        .filter(function (item) { return keys.indexOf(item.key) !== -1 && item.quantity > 0; })
        .map(fromCart);
      receipts.forEach(function (receipt) { receipt.setCart(lines); });
    });

    // The theme and the options app set the variant on the form's
    // [name="id"] and fire `change` on it: natively, or through jQuery.
    function onVariant(target) {
      if (!target || !target.matches || !target.matches('[name="id"]')) return;
      receipts.forEach(function (receipt) {
        if (receipt.scope.contains(target)) receipt.setVariant(String(target.value));
      });
    }
    document.addEventListener('change', function (event) { onVariant(event.target); }, true);
    function withJquery() {
      var $ = root.jQuery;
      if (!$ || !$.fn || !$.fn.on) return;
      $(document).on('change', '[name="id"]', function () { onVariant(this); });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', withJquery, { once: true });
    else withJquery();
  }

  function mount(el) {
    if (!el || !el.hasAttribute || !el.hasAttribute('data-offer-receipt')) return;
    if (el.__offerReceipt || !pricing() || !arm()) return;
    var receipt;
    try {
      receipt = new Receipt(el);
      if (!receipt.draw()) return;
    } catch (error) {
      return;
    }
    el.__offerReceipt = receipt;
    receipts.push(receipt);
    el.setAttribute('data-ready', '');
    // Read out redraws, not the first draw.
    el.setAttribute('aria-live', 'polite');
    bind();
  }

  function mountAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-offer-receipt]'), mount);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAll, { once: true });

  return { plan: plan, groupOf: groupOf, shorten: shorten, describe: describe, moneyFormat: moneyFormat, photoOf: photoOf, mount: mount, THIS_KEY: THIS_KEY, MAX_ROWS: MAX_ROWS };
});
